import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { LucideAngularModule, Plus } from 'lucide-angular';
import { PassportService } from '../../../../core/services/passport.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../store/app.state';
import { Observable, Subscription, take, filter } from 'rxjs';
import { map } from 'rxjs/operators';
import { ModalService } from '../../../../core/services/modal.service';
import { AddContactModalComponent } from '../../../../shared/components/modal/add-contact-modal';
import { PhoneNumberPipe } from '../../../../shared/pipes';

interface Contact {
  firstName: string;
  lastName: string;
  type: string;
  mobileNumber: string;
}

@Component({
  selector: 'app-contacts-and-filiation',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LucideAngularModule,
    PhoneNumberPipe,
  ],
  templateUrl: './contacts-and-filiation.component.html',
  styleUrls: ['./contacts-and-filiation.component.scss'],
})
export class ContactsAndFiliationComponent implements OnInit, OnDestroy {
  readonly Plus = Plus;
  contacts: Contact[] = [];
  loading = false;
  error = '';
  private subscription = new Subscription();

  constructor(
    private passportService: PassportService,
    private store: Store<AppState>,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.setupUserListener();
    this.setupModalListener();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private setupUserListener(): void {
    // Écouter les changements de l'utilisateur et charger les contacts quand il est disponible
    this.subscription.add(
      this.store
        .select((state) => state.auth.currentUser)
        .pipe(
          filter((user) => user !== null && user !== undefined),
          take(1)
        )
        .subscribe({
          next: (currentUser) => {
            console.log('Current user loaded:', currentUser);
            this.loadContactsForUser(currentUser);
          },
          error: (error) => {
            console.error(
              'Erreur lors de la récupération des données utilisateur:',
              error
            );
            this.error =
              'Erreur lors de la récupération des données utilisateur';
          },
        })
    );
  }

  private loadContactsForUser(currentUser: any): void {
    if (!currentUser.passportNumber) {
      console.log('Passport number is missing from user:', currentUser);
      this.error = 'Passport non trouvé dans les données utilisateur';
      return;
    }

    this.loading = true;
    this.error = '';
    console.log('Loading contacts for passport:', currentUser.passportNumber);

    this.passportService.getContacts(currentUser.passportNumber).subscribe({
      next: (response) => {
        console.log('API response:', response);
        this.contacts = this.mapContactsFromApi(response);
        this.loading = false;
      },
      error: (error) => {
        console.error('Erreur lors du chargement des contacts:', error);
        this.error = 'Erreur lors du chargement des contacts';
        this.loading = false;
      },
    });
  }

  loadContacts(): void {
    // Méthode pour recharger les contacts (utilisée après ajout)
    this.store
      .select((state) => state.auth.currentUser)
      .pipe(take(1))
      .subscribe({
        next: (currentUser) => {
          if (currentUser) {
            this.loadContactsForUser(currentUser);
          }
        },
      });
  }

  private mapContactsFromApi(apiResponse: any): Contact[] {
    console.log('Mapping API response:', apiResponse);

    // Adapter cette méthode selon la structure de réponse de votre API
    if (apiResponse && apiResponse.data) {
      return apiResponse.data.map((contact: any) => ({
        firstName: contact.firstName || contact.first_name || '',
        lastName: contact.lastName || contact.last_name || '',
        type: contact.type || contact.contactType || '',
        mobileNumber:
          contact.mobileNumber ||
          contact.phone ||
          contact.mobile ||
          contact.phoneNumber ||
          '',
      }));
    }

    // Fallback si la structure est différente
    if (Array.isArray(apiResponse)) {
      return apiResponse.map((contact: any) => ({
        firstName: contact.firstName || contact.first_name || '',
        lastName: contact.lastName || contact.last_name || '',
        type: contact.type || contact.contactType || '',
        mobileNumber:
          contact.mobileNumber ||
          contact.phone ||
          contact.mobile ||
          contact.phoneNumber ||
          '',
      }));
    }

    return [];
  }

  onAddContact(): void {
    this.modalService.open({
      component: AddContactModalComponent,
      title: 'Ajouter un nouveau contact',
      width: '600px',
      closable: true,
    });
  }

  private setupModalListener(): void {
    // Écouter les événements de fermeture du modal
    this.subscription.add(
      this.modalService.getEvents().subscribe((event) => {
        if (event.type === 'contactAdded') {
          // Recharger les contacts après ajout
          this.loadContacts();
        }
      })
    );

    // Écouter les événements de fermeture du modal et d'ajout de contact
    window.addEventListener('closeModal', () => {
      // Recharger les contacts après fermeture du modal
      this.loadContacts();
    });

    window.addEventListener('contactAdded', () => {
      // Recharger les contacts après ajout d'un contact
      this.loadContacts();
    });
  }
}
